import { ActionIcon, Drawer, Flex, ScrollArea, Text } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconX } from '@tabler/icons-react';
import cn from 'clsx';
import * as React from 'react';

import BaseClickable from '@/components/ui/BaseClickable';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import { STEP_KIND_LABEL, TOOL_CALL_STEP_COLLAPSE_THRESHOLD } from '@/configs/toolCallHistory';
import { useToolCallStepsExpand } from '@/hooks/useToolCallStepsExpand';
import type { ToolCallHistoryStep } from '@/stores/local/shared/ToolCallHistoryStore';
import { removeOrphanedTableFragments, unwrapInlineMarkdownFences } from '@/utils/unwrapMarkdownCodeFence';

import s from './ToolCallHistoryDrawer.module.scss';

type Props = {
  opened: boolean;
  onClose: () => void;
  steps: ToolCallHistoryStep[];
  durationLabel?: string | null;
  /** Выше родительского drawer (например чата в панели документов) */
  zIndex?: number;
  position?: 'left' | 'right';
};

const ToolCallHistoryDrawer: React.FC<Props> = ({
  opened,
  onClose,
  steps,
  durationLabel,
  zIndex,
  position = 'right',
}) => {
  const hasSteps = steps.length > 0;
  const { isStepNew, isStepExpanded, handleStepHeaderClick } = useToolCallStepsExpand(steps);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position={position}
      size={560}
      zIndex={zIndex}
      withCloseButton={false}
      classNames={{ content: s.root, body: s.root__body }}
      overlayProps={{ opacity: 0.4, blur: 2 }}
    >
      <div className={s.root__content}>
        <Flex className={s['root__content-header']} justify="space-between" align="center" gap="sm">
          <div className={s['root__content-header-info']}>
            <Text className={s['root__content-header-title']} fw={700} size="xl">
              Активность
            </Text>
          </div>
          <Flex className={s.root__actions} align="center" gap="xs">
            {durationLabel && (
              <Text size="xs" fw={600} c="dimmed" className={s['root__duration']}>
                {durationLabel}
              </Text>
            )}
            <ActionIcon variant="subtle" color="gray" radius="xl" aria-label="Закрыть историю" onClick={onClose}>
              <IconX size={18} />
            </ActionIcon>
          </Flex>
        </Flex>

        <ScrollArea.Autosize className={s['root__content-list']} mah="100%" scrollbarSize={6} offsetScrollbars>
          {!hasSteps ? (
            <Flex justify="center" align="center" className={s.root__empty}>
              <Text size="sm" c="dimmed">
                Нет записанных шагов.
              </Text>
            </Flex>
          ) : (
            <div className={s.root__items}>
              {steps.map((step, index) => {
                const isToolInteraction = step.kind === 'tool_interaction';
                const contentRaw = isToolInteraction ? step.resultContent : step.text;
                const cleanText = removeOrphanedTableFragments(unwrapInlineMarkdownFences(contentRaw));
                const headerLabel = isToolInteraction ? step.displayName : STEP_KIND_LABEL[step.kind];
                const isCollapsible = cleanText.length >= TOOL_CALL_STEP_COLLAPSE_THRESHOLD;
                const isExpanded = isStepExpanded(step.id);

                return (
                  <div
                    key={step.id}
                    className={cn(s.root__item, isStepNew(step.id) && s.root__item_animate)}
                    style={{ '--item-delay': `${index * 80}ms` } as React.CSSProperties}
                  >
                    <span className={s['root__item-icon']} />
                    <div className={s.root__item_content}>
                      {isCollapsible ? (
                        <BaseClickable
                          className={s['root__item-step-header']}
                          data-step-id={step.id}
                          onClick={handleStepHeaderClick}
                          aria-expanded={isExpanded}
                        >
                          <Text size="sm" fw={600} c="dimmed" className={s['root__item-kind-label']}>
                            {headerLabel}
                          </Text>
                          <span className={s['root__item-step-chevron']}>
                            {isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                          </span>
                        </BaseClickable>
                      ) : (
                        <Text size="sm" fw={600} c="dimmed" className={s['root__item-kind-label']}>
                          {headerLabel}
                        </Text>
                      )}
                      {(!isCollapsible || isExpanded) && (
                        <MarkdownRenderer
                          content={cleanText}
                          className={s.root__markdown}
                          paragraphClassName={s['root__markdown-paragraph']}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea.Autosize>
      </div>
    </Drawer>
  );
};

export default React.memo(ToolCallHistoryDrawer);
